---
title: Purchase Confirmed
---

<script lang="ts">
  export let data;
  import { onMount } from 'svelte';
  import { track } from '$lib/analytics';

  // Fire analytics event if brand has product and plausibleDomain
  onMount(() => {
    if (data.config.productName && data.config.analytics?.plausibleDomain) {
      track('PurchaseConfirmed', {
        product: data.config.productName,
        brand: data.config.brand
      });
    }
  });

  // Handle download link click with analytics tracking
  function handleDownloadClick(event: Event) {
    const target = event.target as HTMLAnchorElement;
    const downloadUrl = target.href;

    // Fire PurchaseDownloaded event if brand has product and plausibleDomain
    if (data.config.product?.downloadUrl && data.config.analytics?.plausibleDomain) {
      track('PurchaseDownloaded', {
        product: data.config.product.name,
        brand: data.config.brand,
        downloadUrl: downloadUrl
      });
    }

    // Allow the default download behavior to proceed
    // No need to preventDefault() - we want the file to download
  }
</script>

{#if data.config.productName}
  <div class="purchase-success">
    <div class="success-icon">🎉</div>
    <h1>Thank you for your purchase!</h1>
    <p class="product-message">
      We're excited that you've purchased <strong>{data.config.productName}</strong> from {data.config.displayName}.
    </p>

    {#if data.config.product?.downloadUrl}
      <div class="download-section">
        <h2>Your Download is Ready!</h2>
        <p class="download-message">
          Click the button below to download your copy of <strong>{data.config.product.name}</strong>.
        </p>
        <a href={data.config.product.downloadUrl} class="download-button" target="_blank" rel="noopener noreferrer" on:click={handleDownloadClick}>
          📥 Download {data.config.product.name}
        </a>
      </div>
    {/if}

    <div class="next-steps">
      <h2>What's Next?</h2>
      <ul>
        <li>Check your email for purchase confirmation and access details</li>
        {#if data.config.product?.downloadUrl}
          <li>Download your product files using the button above</li>
        {:else}
          <li>Download your product files if applicable</li>
        {/if}
        <li>Add us to your contacts to ensure you receive important updates</li>
        <li>Feel free to <a href="/contact/">contact us</a> if you have any questions</li>
      </ul>
    </div>

    <div class="support-info">
      <p>
        If you have any questions or need support, please don't hesitate to
        <a href="/contact/">reach out to us</a>. We're here to help you get the most out of your purchase.
      </p>
    </div>
  </div>
{:else}
  <div class="no-product-message">
    <div class="info-icon">ℹ️</div>
    <h1>Thank you for your interest!</h1>
    <p class="polite-message">
      We appreciate your visit to {data.config.displayName}. While we don't currently have a product available for purchase,
      we'd love to keep you updated on our latest offerings and developments.
    </p>

    <div class="alternative-actions">
      <h2>Stay Connected</h2>
      <ul>
        <li><a href="/">Visit our homepage</a> to learn more about what we do</li>
        <li><a href="/about/">Read about us</a> and our mission</li>
        <li><a href="/contact/">Get in touch</a> if you'd like to be notified about future products</li>
      </ul>
    </div>

    <div class="newsletter-info">
      <p>
        Interested in staying updated? <a href="/landing/">Sign up for our newsletter</a>
        to be the first to know about new products and opportunities.
      </p>
    </div>
  </div>
{/if}

<style>
  .purchase-success,
  .no-product-message {
    text-align: center;
    padding: 2rem;
    max-width: 600px;
    margin: 0 auto;
  }

  .success-icon,
  .info-icon {
    font-size: 4rem;
    margin-bottom: 1rem;
  }

  h1 {
    color: var(--brand-primary);
    font-size: 2.5rem;
    margin-bottom: 1.5rem;
  }

  .product-message,
  .polite-message {
    font-size: 1.25rem;
    color: #374151;
    margin-bottom: 2rem;
    line-height: 1.6;
  }

  .download-section {
    background: linear-gradient(135deg, rgba(56, 189, 248, 0.1), rgba(30, 41, 59, 0.1));
    border: 2px solid var(--brand-secondary);
    border-radius: 16px;
    padding: 2.5rem;
    margin: 2rem 0;
    text-align: center;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  }

  .download-section h2 {
    color: var(--brand-primary);
    font-size: 1.75rem;
    margin-bottom: 1rem;
  }

  .download-message {
    color: #374151;
    font-size: 1.1rem;
    margin-bottom: 1.5rem;
    line-height: 1.5;
  }

  .download-button {
    display: inline-block;
    background: linear-gradient(135deg, var(--color--primary, var(--brand-primary)), var(--color--accent, var(--brand-secondary)));
    color: rgba(255, 255, 255, 0.9) !important;
    padding: 1rem 2rem;
    border-radius: 12px;
    text-decoration: none;
    font-weight: 600;
    font-size: 1.1rem;
    border: none;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
    border-bottom: none;
  }

  .download-button:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
    color: rgba(255, 255, 255, 0.95) !important;
    border-bottom: none;
  }

  .download-button:active {
    transform: translateY(0);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  }

  .next-steps,
  .alternative-actions {
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    padding: 2rem;
    margin: 2rem 0;
    text-align: left;
  }

  .next-steps h2,
  .alternative-actions h2 {
    color: var(--brand-primary);
    font-size: 1.5rem;
    margin-bottom: 1rem;
    text-align: center;
  }

  .next-steps ul,
  .alternative-actions ul {
    list-style: none;
    padding: 0;
  }

  .next-steps li,
  .alternative-actions li {
    padding: 0.75rem 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    position: relative;
    padding-left: 2rem;
  }

  .next-steps li:before,
  .alternative-actions li:before {
    content: "✓";
    position: absolute;
    left: 0;
    color: var(--brand-secondary);
    font-weight: bold;
  }

  .next-steps li:last-child,
  .alternative-actions li:last-child {
    border-bottom: none;
  }

  .support-info,
  .newsletter-info {
    margin-top: 2rem;
    padding: 1.5rem;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 8px;
    border-left: 4px solid var(--brand-secondary);
  }

  .support-info p,
  .newsletter-info p {
    color: #64748b;
    margin: 0;
    font-style: italic;
  }

  a {
    color: var(--brand-primary);
    text-decoration: none;
    border-bottom: 1px solid var(--brand-primary);
    transition: all 0.3s ease;
  }

  a:hover {
    color: var(--brand-secondary);
    border-bottom-color: var(--brand-secondary);
  }

  @media (max-width: 768px) {
    .purchase-success,
    .no-product-message {
      padding: 1rem;
    }

    h1 {
      font-size: 2rem;
    }

    .product-message,
    .polite-message {
      font-size: 1.1rem;
    }

    .download-section {
      padding: 2rem 1.5rem;
    }

    .download-section h2 {
      font-size: 1.5rem;
    }

    .download-message {
      font-size: 1rem;
    }

    .download-button {
      padding: 0.875rem 1.5rem;
      font-size: 1rem;
    }

    .next-steps,
    .alternative-actions {
      padding: 1.5rem;
    }
  }
</style>
